/**
 * Master switch for certification display. Currently OFF.
 *
 * Nothing certification-related renders anywhere on the site while this is
 * false: no band on the practice hub, no strip on the services overview, no
 * inline badge beside a service's problem statement, and no "Backed by" line
 * on a service card. The data below and the CertificationBadges component are
 * kept intact rather than deleted, so this is a one-line change to bring back
 * once there is a decision to display them.
 *
 * The `certification` field on each Service is also kept -- it records which
 * credential backs which service regardless of whether that is shown, and the
 * taxonomy test still checks every code resolves to a real entry here.
 */
// Annotated `boolean` rather than left to infer the literal type `false`.
// Without it TypeScript narrows every guarded branch to unreachable, stops
// type-checking the markup inside, and flipping this to true would surface a
// batch of errors that were invisible while it was off.
export const SHOW_CERTIFICATIONS: boolean = false;

export interface Certification {
  /** ServiceNow's official abbreviation, e.g. 'CSA', 'CAD', 'CIS-DF'. */
  code: string;
  name: string;
  /**
   * The individual who earned it. A certification belongs to a person, not to
   * a company -- see the TODO below.
   */
  holder: string | null;
}

/**
 * The ServiceNow certifications this team holds. Each one is a real, named
 * credential, which is what makes it usable as proof next to a service.
 *
 * TODO (needs a human): every `holder` is null. ServiceNow certifications are
 * awarded to individuals, so each entry below needs the name of the person who
 * actually earned it before this reads as verifiable. Do not fill these in by
 * inference.
 *
 * Two things this module deliberately does not do, both of them existing bugs
 * the audit called out:
 *   - It does not expose a count. "8+ Certified Professionals" appears on the
 *     homepage with nothing behind it; nothing here should let a headcount be
 *     rendered from an array length, because the array is a list of
 *     certifications, not of people. One person may hold several.
 *   - It does not carry company-level partner status. "ServiceNow Partner" was
 *     listed as a personal skill on a team member's card in content/about.ts;
 *     that has been removed rather than moved here. Partner status is a
 *     company relationship and needs a human to decide where it belongs.
 */
export const CERTIFICATIONS: Certification[] = [
  { code: 'CSA', name: 'Certified System Administrator', holder: null },
  { code: 'CAD', name: 'Certified Application Developer', holder: null },
  {
    code: 'CIS-DF',
    name: 'Certified Implementation Specialist, Data Foundations',
    holder: null,
  },
  {
    code: 'CIS-CSM',
    name: 'Certified Implementation Specialist, Customer Service Management',
    holder: null,
  },
  {
    code: 'CIS-ITSM',
    name: 'Certified Implementation Specialist, IT Service Management',
    holder: null,
  },
];

/** Looks up a certification by the code a Service references. */
export function getCertificationByCode(code: string): Certification | undefined {
  return CERTIFICATIONS.find((certification) => certification.code === code);
}
